import { useMemo } from "react";
import type { FinancialPeriodFilter, FinancialPeriodPreset } from "@/core/analytics/financial-period";
import { defaultFinancialPeriodFilter, listFinancialYearOptions } from "@/core/analytics/financial-period";

interface FinancialPeriodFilterBarProps {
  value: FinancialPeriodFilter;
  onChange: (next: FinancialPeriodFilter) => void;
  yearOptions?: number[];
}

const PRESETS: { id: FinancialPeriodPreset; label: string }[] = [
  { id: "today", label: "Jour" },
  { id: "week", label: "7 jours" },
  { id: "month", label: "Mois" },
  { id: "year", label: "Année" },
  { id: "custom", label: "Personnalisé" },
];

const MONTH_OPTIONS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function FinancialPeriodFilterBar({
  value,
  onChange,
  yearOptions,
}: FinancialPeriodFilterBarProps) {
  const years = useMemo(
    () => listFinancialYearOptions(yearOptions ?? []),
    [yearOptions],
  );

  const setPreset = (preset: FinancialPeriodPreset) => {
    const base = defaultFinancialPeriodFilter();
    if (preset === "today" || preset === "week") {
      onChange({ preset, date: value.date ?? todayIso() });
      return;
    }
    if (preset === "month") {
      onChange({
        preset,
        month: value.month ?? base.month,
        year: value.year ?? base.year,
      });
      return;
    }
    if (preset === "year") {
      onChange({ preset, year: value.year ?? base.year });
      return;
    }
    onChange({
      preset: "custom",
      dateFrom: value.dateFrom ?? addDaysIso(todayIso(), -29),
      dateTo: value.dateTo ?? todayIso(),
    });
  };

  return (
    <div className="ph-invoice-sheet ph-dash-card mb-4 ph-fin-period">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h2 className="h6 mb-1 fw-semibold">Période d&apos;analyse</h2>
          <p className="text-muted small mb-0">
            Filtrez les revenus, la trésorerie et les répartitions par jour, mois ou année.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => onChange(defaultFinancialPeriodFilter())}
        >
          Réinitialiser
        </button>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3" role="group" aria-label="Préréglages période">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`btn btn-sm ${value.preset === preset.id ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setPreset(preset.id)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="row g-2 align-items-end">
        {value.preset === "today" && (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label className="form-label small text-muted mb-1">Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={value.date ?? todayIso()}
              onChange={(e) => onChange({ ...value, date: e.target.value })}
            />
          </div>
        )}

        {value.preset === "week" && (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label className="form-label small text-muted mb-1">Fin de période (7 jours)</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={value.date ?? todayIso()}
              onChange={(e) => onChange({ ...value, date: e.target.value })}
            />
          </div>
        )}

        {value.preset === "month" && (
          <>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label className="form-label small text-muted mb-1">Mois</label>
              <select
                className="form-select form-select-sm"
                value={value.month ?? new Date().getMonth() + 1}
                onChange={(e) =>
                  onChange({ ...value, month: Number(e.target.value) })
                }
              >
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label className="form-label small text-muted mb-1">Année</label>
              <select
                className="form-select form-select-sm"
                value={value.year ?? new Date().getFullYear()}
                onChange={(e) =>
                  onChange({ ...value, year: Number(e.target.value) })
                }
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {value.preset === "year" && (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label className="form-label small text-muted mb-1">Année</label>
            <select
              className="form-select form-select-sm"
              value={value.year ?? new Date().getFullYear()}
              onChange={(e) =>
                onChange({ ...value, year: Number(e.target.value) })
              }
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        )}

        {value.preset === "custom" && (
          <>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label className="form-label small text-muted mb-1">Du</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={value.dateFrom ?? addDaysIso(todayIso(), -29)}
                onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label className="form-label small text-muted mb-1">Au</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={value.dateTo ?? todayIso()}
                onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
